import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Editor from '@monaco-editor/react';
import { Loader2, Play, Send, Code2, CheckCircle, XCircle, X, ArrowRight, Clock } from 'lucide-react';

const LANGS = [
  { id: 'python', label: 'Python' },
  { id: 'javascript', label: 'JavaScript' },
];

const fmtT = (s) => `${Math.floor(Math.max(0, s) / 60)}:${String(Math.max(0, s) % 60).padStart(2, '0')}`;

export const VerifiedInterviewCoding = ({ API, token, attemptId, onClose, onAiMessage, timeLimit = 600 }) => {
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [loading, setLoading] = useState(true);
  const [challenge, setChallenge] = useState(null);
  const [language, setLanguage] = useState('python');
  const [code, setCode] = useState('');
  const [stdin, setStdin] = useState('');
  const [running, setRunning] = useState(false);
  const [runOut, setRunOut] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.post(`${API}/api/verified-talent/attempts/${attemptId}/coding/generate`, {}, { ...H, timeout: 90000 });
        const ch = res.data.challenge;
        setChallenge(ch);
        setLanguage(ch.language || 'python');
        setCode(ch.starter_code || '');
        setStdin((ch.examples && ch.examples[0] && ch.examples[0].input) || '');
        setTimeLeft(timeLimit);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not load the coding challenge');
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const runCode = async () => {
    if (running || submitting) return;
    setRunning(true); setRunOut(null);
    try {
      const res = await axios.post(`${API}/api/verified-talent/attempts/${attemptId}/coding/run`, { code, language, stdin }, { ...H, timeout: 60000 });
      setRunOut(res.data);
    } catch (e) {
      setRunOut({ status: 'Error', stderr: e.response?.data?.detail || 'Run failed' });
    } finally {
      setRunning(false);
    }
  };

  const submitCode = async () => {
    if (submitting || running) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/verified-talent/attempts/${attemptId}/coding/submit`, { code, language }, { ...H, timeout: 120000 });
      setResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const continueInterview = () => {
    if (result?.followup) onAiMessage(result.followup);
    onClose();
  };

  // Countdown → auto-submit when time runs out
  useEffect(() => {
    if (timeLeft === null || result || loading) return;
    if (timeLeft <= 0) { if (!submitting) submitCode(); return; }
    const id = setInterval(() => setTimeLeft((t) => (t === null ? t : t - 1)), 1000);
    return () => clearInterval(id);
  }, [timeLeft, result, loading, submitting]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 md:p-6" data-testid="coding-sandbox-modal">
      <div className="w-full max-w-6xl h-[90vh] rounded-2xl border border-white/10 bg-[#0A0A0F] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-white/[0.02]">
          <div className="flex items-center gap-2 min-w-0">
            <Code2 size={18} className="text-purple-400 flex-shrink-0" />
            <span className="font-semibold text-white truncate">{challenge?.title || 'Live Coding Challenge'}</span>
            {challenge?.difficulty && (
              <span className="px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wide bg-white/10 text-zinc-300">{challenge.difficulty}</span>
            )}
          </div>
          {!result && (
            <div className="flex items-center gap-3">
              {timeLeft !== null && (
                <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded ${timeLeft <= 60 ? 'bg-red-500/15 text-red-300' : 'bg-white/10 text-zinc-300'}`} data-testid="coding-timer">
                  <Clock size={12} /> {fmtT(timeLeft)}
                </span>
              )}
              <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="coding-close-btn" title="Close (this activity is optional)">
                <X size={18} />
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-zinc-400 gap-2"><Loader2 className="animate-spin" size={18} /> Preparing your coding problem…</div>
        ) : result ? (
          <div className="flex-1 overflow-auto p-6" data-testid="coding-result">
            <div className="max-w-2xl mx-auto space-y-5 text-center">
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${result.pass_rate >= 60 ? 'bg-emerald-500/15' : 'bg-amber-500/15'}`}>
                {result.pass_rate >= 60 ? <CheckCircle size={30} className="text-emerald-400" /> : <XCircle size={30} className="text-amber-400" />}
              </div>
              <h3 className="text-2xl font-bold text-white">{result.tests_passed}/{result.tests_total} tests passed</h3>
              {result.evaluation?.scores && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-left">
                  {Object.entries(result.evaluation.scores).map(([k, v]) => (
                    <div key={k} className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wide text-zinc-500 capitalize">{k.replace(/_/g, ' ')}</p>
                      <p className="text-lg font-bold text-white">{Math.round(Number(v) || 0)}<span className="text-xs text-zinc-500">/100</span></p>
                    </div>
                  ))}
                </div>
              )}
              {result.evaluation?.summary && (
                <p className="text-sm text-zinc-300 leading-relaxed text-left rounded-xl border border-white/10 bg-white/[0.02] p-4">{result.evaluation.summary}</p>
              )}
              <button onClick={continueInterview} className="inline-flex items-center gap-2 px-6 py-2.5 bg-purple-600 hover:bg-purple-500 rounded-lg text-sm font-bold" data-testid="coding-continue-btn">
                Continue interview <ArrowRight size={15} />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 min-h-0">
            {/* Problem panel */}
            <div className="overflow-auto p-5 border-b lg:border-b-0 lg:border-r border-white/10">
              <p className="text-[11px] uppercase tracking-widest text-purple-300 font-bold mb-2">Problem</p>
              <div className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed" data-testid="coding-prompt">{challenge?.prompt}</div>
              {challenge?.complexity_target && (
                <p className="mt-3 text-xs text-zinc-400">Target complexity: <span className="text-zinc-200 font-mono">{challenge.complexity_target}</span></p>
              )}
              {(challenge?.examples || []).length > 0 && (
                <div className="mt-4 space-y-3">
                  <p className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">Examples</p>
                  {challenge.examples.map((ex, i) => (
                    <div key={i} className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-xs font-mono">
                      <div className="text-zinc-500">Input</div>
                      <pre className="text-zinc-200 whitespace-pre-wrap">{ex.input}</pre>
                      <div className="text-zinc-500 mt-2">Output</div>
                      <pre className="text-emerald-300 whitespace-pre-wrap">{ex.output}</pre>
                      {ex.explanation && <div className="text-zinc-500 mt-2 font-sans">{ex.explanation}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Editor panel */}
            <div className="flex flex-col min-h-0">
              <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 bg-white/[0.02]">
                <select value={language} onChange={(e) => setLanguage(e.target.value)} className="bg-[#141420] text-zinc-200 text-xs rounded-md border border-white/10 px-2 py-1" data-testid="coding-language-select">
                  {LANGS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                </select>
                <div className="flex items-center gap-2">
                  <button onClick={runCode} disabled={running || submitting} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/15 text-xs font-semibold disabled:opacity-50" data-testid="coding-run-btn">
                    {running ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} Run
                  </button>
                  <button onClick={submitCode} disabled={submitting || running} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 text-xs font-bold disabled:opacity-50" data-testid="coding-submit-btn">
                    {submitting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Submit
                  </button>
                </div>
              </div>
              <div className="flex-1 min-h-[200px]">
                <Editor
                  height="100%"
                  language={language === 'javascript' ? 'javascript' : 'python'}
                  theme="vs-dark"
                  value={code}
                  onChange={(v) => setCode(v || '')}
                  options={{ minimap: { enabled: false }, fontSize: 13, scrollBeyondLastLine: false }}
                />
              </div>
              <div className="border-t border-white/10 p-3 bg-white/[0.02]">
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Custom input (stdin)</label>
                <textarea value={stdin} onChange={(e) => setStdin(e.target.value)} rows={2} className="w-full mt-1 bg-[#141420] text-zinc-200 text-xs rounded-md border border-white/10 px-2 py-1.5 font-mono resize-none" data-testid="coding-stdin" />
                {runOut && (
                  <div className="mt-2 text-xs font-mono" data-testid="coding-run-output">
                    <span className={`font-bold ${runOut.status === 'Accepted' ? 'text-emerald-400' : 'text-amber-400'}`}>{runOut.status}</span>
                    {runOut.stdout && <pre className="text-zinc-200 whitespace-pre-wrap mt-1">{runOut.stdout}</pre>}
                    {runOut.stderr && <pre className="text-red-300 whitespace-pre-wrap mt-1">{runOut.stderr}</pre>}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifiedInterviewCoding;
